import React, { useRef, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { Layout } from "views/common/Layout";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "views/components/ui/Card";
import { Loader2, ArrowRight, Wrench } from "lucide-react";
import { fetchCategories } from "actions/category";
import { Link } from "react-router-dom";

const CategoryPage = (props) => {
    const [isLoading, setIsLoading] = useState(true);
    const categoryRefs = useRef({});

    useEffect(() => {
        props.fetchCategories();
        setIsLoading(false);
    }, []);

    const scrollToCategory = (categoryId) => {
        const element = categoryRefs.current[categoryId];
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    if (isLoading) {
        return (
            <Layout>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex justify-center items-center min-h-[50vh]">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    </div>
                </div>
            </Layout>
        );
    }

    // Convert category map to array for easier rendering
    const categoriesArray = Object.values(props.categories);
    const allCategories = categoriesArray.map((cat) => cat.name).join(", ");
    const allSubCategories = categoriesArray
        .flatMap((cat) => cat.subCategories.map((sub) => sub.name))
        .join(", ");

    return (
        <Layout>
            <Helmet>
                <title>Browse AI Tools by Category | Launch Hero FYI</title>
                <meta
                    name="description"
                    content={`Explore our comprehensive collection of AI tools organized by categories. Find tools in ${allCategories} and more. Browse through ${categoriesArray.length} categories and discover the perfect AI solution for your needs.`}
                />
                <meta
                    name="keywords"
                    content={`${allCategories}, ${allSubCategories}, AI tools, software categories, tool directory, AI solutions`}
                />
                <meta
                    property="og:title"
                    content="Browse AI Tools by Category | Launch Hero FYI"
                />
                <meta
                    property="og:description"
                    content={`Explore our comprehensive collection of AI tools organized by categories. Find tools in ${allCategories} and more. Browse through ${categoriesArray.length} categories and discover the perfect AI solution for your needs.`}
                />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta name="twitter:card" content="summary" />
                <meta
                    name="twitter:title"
                    content="Browse AI Tools by Category | Launch Hero FYI"
                />
                <meta
                    name="twitter:description"
                    content={`Explore our comprehensive collection of AI tools organized by categories. Find tools in ${allCategories} and more. Browse through ${categoriesArray.length} categories and discover the perfect AI solution for your needs.`}
                />
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold tracking-tight">
                        Categories
                    </h1>
                    <p className="mt-2 text-muted-foreground">
                        Browse through our collection of tools organized by
                        categories
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    {/* Navigation Sidebar */}
                    <div className="md:col-span-1">
                        <div className="bg-white rounded-lg border border-gray-200 p-4 sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
                            <h2 className="text-lg font-semibold mb-4">
                                Navigation
                            </h2>
                            <nav className="space-y-2">
                                {categoriesArray.map((category) => (
                                    <button
                                        key={category.id}
                                        onClick={() =>
                                            scrollToCategory(category.id)
                                        }
                                        className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 transition-colors flex items-center gap-2"
                                    >
                                        {category.name}
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </div>

                    {/* Categories Grid */}
                    <div className="md:col-span-3 space-y-6">
                        {categoriesArray.map((category) => (
                            <div
                                key={category.id}
                                ref={(el) =>
                                    (categoryRefs.current[category.id] = el)
                                }
                                id={category.id}
                                className="scroll-mt-20"
                            >
                                <Card className="overflow-hidden">
                                    <CardHeader>
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-lg">
                                                <Wrench className="h-6 w-6" />
                                            </div>
                                            <CardTitle>
                                                {category.name}
                                            </CardTitle>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="px-4 py-2">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {category.subCategories.map(
                                                (subCategory) => (
                                                    <Link
                                                        key={subCategory.id}
                                                        to={`/category/${subCategory.name}`}
                                                        className="group p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-500 transition-colors cursor-pointer block"
                                                    >
                                                        <div className="flex items-center justify-between mb-2">
                                                            <h3 className="font-medium group-hover:text-blue-600 transition-colors">
                                                                {
                                                                    subCategory.name
                                                                }
                                                            </h3>
                                                            <span className="text-sm text-gray-500">
                                                                {
                                                                    subCategory.count
                                                                }{" "}
                                                                tools
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                                            <ArrowRight className="h-4 w-4 opacity-1000 group-hover:opacity-100 transition-opacity" />
                                                            <span>
                                                                View details
                                                            </span>
                                                        </div>
                                                    </Link>
                                                )
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

const mapStateToProps = (state) => ({
    categories: state.category.categories,
});

export default connect(mapStateToProps, { fetchCategories })(CategoryPage);
